import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { DocumentServiceGateway } from 'src/app/application/gateway/DocumentServiceGateway';
import { LocationType } from 'src/app/domain/enums/location-type.enum';
import { Delivery } from 'src/app/domain/models/Delivery';
import { DeliveryCost } from 'src/app/domain/models/DeliveryCost';
import { DeliveryDiscount } from 'src/app/domain/models/DeliveryDiscount';
import { GenericOrderDetail, ImportOrder } from 'src/app/domain/models/ImportOrder';
import { Payment } from 'src/app/domain/models/Payment';
import { PaymentQualityControl } from 'src/app/domain/models/PaymentQualityControl';
import { BasicUser } from 'src/app/domain/models/User';
import { Routes } from 'src/app/infrastructure/routes';

interface DiscountModel {
  originDiscounts: DeliveryDiscount[]
  destinationDiscounts: DeliveryDiscount[]
}

interface CostModel {
  originCosts: DeliveryCost[]
  destinationCosts: DeliveryCost[]
}

@Component({
  selector: 'app-import-order-payments',
  templateUrl: './import-order-payments.component.html',
  styleUrls: ['./import-order-payments.component.css']
})
export class ImportOrderPaymentsComponent implements OnInit {
  @Input()
  payments: Payment[] = []

  @Input()
  discounts: DeliveryDiscount[] = []

  @Input()
  costs: DeliveryCost[] = []

  @Input()
  delivery: Delivery | undefined

  @Input()
  importOrderDetails: GenericOrderDetail[] = []

  @Input()
  importOrder: ImportOrder | undefined

  profile: BasicUser | undefined

  loading: boolean = false

  @Input()
  paymentsQualityControl: PaymentQualityControl[] =[]

  @Input()
  isClosedSale: boolean = false

  @Input()
  directSaleProgramId: number | undefined

  discountsList: DiscountModel = {
    originDiscounts: [],
    destinationDiscounts: []
  }
  costsList: CostModel = {
    originCosts: [],
    destinationCosts: []
  }

  constructor(
    private _router: Router,
    private _documentService: DocumentServiceGateway,
    private _authService: AuthGateway
    ){

  }

  ngOnInit(): void {
    this.profile = this._authService.getUser()
    this.getCostsList()
    this.getDiscountsList()
  }

  getCostsList() {
    this.costsList.originCosts = this.getLocationTypeValues(LocationType.Origen, this.costs)
    this.costsList.destinationCosts = this.getLocationTypeValues(LocationType.Destino, this.costs)
    this.costsList.destinationCosts.sort((a, b) => a.currencyCode.localeCompare(b.currencyCode))
  }

  getDiscountsList() {
    this.discountsList.originDiscounts = this.getLocationTypeValues(LocationType.Origen, this.discounts)
    this.discountsList.destinationDiscounts = this.getLocationTypeValues(LocationType.Destino, this.discounts)
    this.discountsList.destinationDiscounts.forEach((el) => el.currencyCode ? el.currencyCode : 'USD')
    this.discountsList.destinationDiscounts.sort((a, b) => a.currencyCode!.localeCompare(b.currencyCode!))
  }

  getLocationTypeValues(locationType: string, data: any[]) {
    return data.filter((el) => el.locationType === locationType)
  }

  goToPayments() {
    if(this.importOrder) {
      if(this.isClosedSale) {
        this._router.navigate(['/' + Routes.userClosedSalePaymentsPage, this.importOrder.guid, this.directSaleProgramId])
      }
      else
      {
        this._router.navigate(['/' + Routes.userPaymentsPage, this.importOrder.guid])
      }
    }
  }

  getOrderTotalPrice(): number {
    let total = 0
    if(this.importOrderDetails) {
      this.importOrderDetails.forEach(x => {
        total += this.getDetailTotalPrice(x)
      })
    }
    return total
  }

  getPaymentDocument(paymentId: number) {
    this._documentService.getPaymentDocument(paymentId).subscribe((resultBlob: any) => {
      var downloadURL = window.URL.createObjectURL(resultBlob.body);
      window.open(downloadURL);})
  }


  getDetailTotalPrice(detail: GenericOrderDetail): number {
    return detail.palletsQuantity > 0 && detail.boxesPerPallet > 0 ? detail.palletsQuantity * detail.boxesPerPallet * detail.price : detail.boxesQuantity * detail.price
  }

  get qcReviewCost(): number {
    let total = 0
    if(this.importOrderDetails) {
      this.importOrderDetails.forEach(x => {
        let tax = x.qcTaxPrice ?  x.qcTaxPrice : 0
        total += x.qcGeneratedPrice ? x.qcGeneratedPrice + tax  : 0
      })
    }
    return total
  }

  getPalletPrice()
  {
    let total = 0

    if(this.importOrderDetails) {
      this.importOrderDetails.forEach(x => {
        total += this.getDetailPalletPrice(x)
      })
    }
    return total
  }

  getDetailPalletPrice(detail: GenericOrderDetail): number {
    let total = 0

    if(detail.palletPrices)
    {
      detail.palletPrices.forEach(x => {
        total += x.price ? x.boxPerPallets * x.price : 0;
      })
      return total
    }
    return 0
  }

  // Valida si hay costos estáticos para mostrar la sección
  get hasStaticCosts(): boolean {
    return ((this.delivery?.oceanFreight ?? 0) + 
      (this.delivery?.inland ?? 0) + 
      (this.delivery?.localCharges ?? 0) + 
      (this.delivery?.customs ?? 0) + 
      (this.delivery?.surcharge ?? 0) + 
      (this.delivery?.handling ?? 0) + 
      (this.delivery?.gateOut ?? 0)) > 0
  }

  // Valida si hay costos estáticos (nuevos), si hay costos QC o si hay costos dinámicos para mostrar la sección
  get hasOtherCosts(): boolean {
    return ((this.delivery?.otherCosts ?? 0) + 
      (this.delivery?.atmControl ?? 0) + 
      (this.delivery?.co2Reduction ?? 0) + 
      (this.delivery?.lowSulfurSurcharge ?? 0) + 
      (this.delivery?.advancedManifestDeclaration ?? 0) + 
      (this.delivery?.bunkerAdjustmentFactor ?? 0) +
      (this.qcReviewCost)) > 0 ||
      this.costsList.originCosts.length > 0
  }

  get totalCost(): number {

    let orderPrice = !this.isClosedSale ? this.getOrderTotalPrice() : this.getPalletPrice()
    if(this.delivery) {
      return orderPrice +
        (this.delivery.oceanFreight ? this.delivery.oceanFreight : 0) +
        (this.delivery.inland ? this.delivery.inland : 0) +
        (this.delivery.localCharges ? this.delivery.localCharges : 0) +
        (this.delivery.customs ? this.delivery.customs : 0) +
        (this.delivery.otherCosts ? this.delivery.otherCosts : 0) +
        (this.delivery.surcharge ? this.delivery.surcharge : 0) +
        (this.delivery.handling ? this.delivery.handling : 0) +
        (this.delivery.gateOut ? this.delivery.gateOut : 0) +
        (this.delivery.atmControl ? this.delivery.atmControl : 0) +
        (this.delivery.co2Reduction ? this.delivery.co2Reduction : 0) +
        (this.delivery.lowSulfurSurcharge ? this.delivery.lowSulfurSurcharge : 0) +
        (this.delivery.advancedManifestDeclaration ? this.delivery.advancedManifestDeclaration : 0) +
        (this.delivery.bunkerAdjustmentFactor ? this.delivery.bunkerAdjustmentFactor : 0) +
        this.qcReviewCost +
        this.totalCosts
    }
    return 0
  }

  get totalPayments(): number {
    let total = 0
    if(this.payments) {
      this.payments.forEach(x => {
        total += x.amount
      })
    }
    return total
  }

  get totalPaymentsQc(): number {
    let total = 0
    if(this.paymentsQualityControl){
      this.paymentsQualityControl.forEach(x => {
        total += x.value;
      })
    }
    return total
  }

  get balance(): number {
    return this.totalPayments - this.totalCost + this.totalDiscounts
  }

  get absoluteBalance(): number {
    return Math.abs(this.balance)
  }

  get totalDiscounts(): number {
    let total = 0
    if(this.discountsList.originDiscounts) {
      this.discountsList.originDiscounts.forEach(x => {
        total += x.amount
      })
    }
    return total
  }

  get totalCosts(): number {
    let total = 0
    if(this.costsList.originCosts) {
      this.costsList.originCosts.forEach(x => {
        total += x.amount
      })
    }
    return total
  }

  get totalDestinationCosts() {
    const grouped = Object.values(
      this.costsList.destinationCosts.reduce((acc, cost) => {
        const key = cost.currencyCode;
        if (!acc[key]) {
          acc[key] = {
            description: cost.description,
            currencyCode: key,
            totalAmount: 0
          };
        }
        acc[key].totalAmount += cost.amount;
        return acc;
      }, {} as Record<string, { description: string, currencyCode: string; totalAmount: number }>)
    );

    return grouped;
  }

  get totalDestinationDiscounts() {
    const grouped = Object.values(
      this.discountsList.destinationDiscounts.reduce((acc, discount) => {
        const key = discount.currencyCode ?? 'USD';
        if (!acc[key]) {
          acc[key] = {
            description: discount.description,
            currencyCode: key,
            totalAmount: 0
          };
        }
        acc[key].totalAmount += discount.amount;
        return acc;
      }, {} as Record<string, { description: string, currencyCode: string; totalAmount: number }>)
    );

    return grouped;
  }
}
